import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { SURVEY_CATEGORIES, SURVEY_SERVICE_TYPES, ServiceRequest } from '../../types';

export const AdminCategories: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(searchParams.get('cat'));
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  useEffect(() => {
    adminService.listRequests({ page: 1 })
      .then((res) => setRequests(res.results ?? []))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  // Count requests per service type
  const reqByType: Record<string, number> = {};
  requests.forEach((r) => {
    reqByType[r.service_type] = (reqByType[r.service_type] || 0) + 1;
  });

  const getCategoryRequestCount = (cat: typeof SURVEY_CATEGORIES[number]) =>
    cat.services.reduce((sum, s) => sum + (reqByType[s] || 0), 0);

  const totalRequests = requests.length;

  const shown = activeCategory
    ? SURVEY_CATEGORIES.filter((c) => c.id === activeCategory)
    : SURVEY_CATEGORIES;

  return (
    <div style={{ padding: '4px' }}>
      {/* ── Page Header ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.pageTitle}>Service Categories</h1>
          <p style={s.pageSubtitle}>
            Platform-wide catalogue of all survey service categories. Monitor usage, manage service offerings, and track demand per category.
          </p>
        </div>
        <div style={s.statsRow}>
          {[
            { label: 'Categories', value: SURVEY_CATEGORIES.length, color: 'var(--sc-brand-primary)' },
            { label: 'Total Services', value: SURVEY_SERVICE_TYPES.length, color: 'var(--sc-success)' },
            { label: 'Requests', value: totalRequests, color: 'var(--sc-warning)' },
          ].map((stat) => (
            <div key={stat.label} style={s.statPill}>
              <span style={{ ...s.statNum, color: stat.color }}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Filter Tabs ── */}
      <div style={s.filterRow}>
        <button
          style={{ ...s.filterTab, ...(activeCategory === null ? s.filterTabActive : {}) }}
          onClick={() => setActiveCategory(null)}
        >
          All
        </button>
        {SURVEY_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            style={{
              ...s.filterTab,
              ...(activeCategory === cat.id
                ? { ...s.filterTabActive, borderColor: cat.color, color: cat.color, background: cat.color + '18' }
                : {}),
            }}
            onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
          >
            {cat.icon} {cat.name.split(' ')[0]}
          </button>
        ))}
      </div>

      {/* ── Summary Bar ── */}
      <div style={s.summaryBar}>
        {SURVEY_CATEGORIES.map((cat) => {
          const count = getCategoryRequestCount(cat);
          const pct = totalRequests > 0 ? (count / totalRequests) * 100 : 0;
          return (
            <div key={cat.id} style={s.summarySegment} title={`${cat.name}: ${count} requests (${pct.toFixed(0)}%)`}>
              <div style={s.summaryIcon}>{cat.icon}</div>
              <div style={s.summaryMeta}>
                <div style={s.summaryName}>{cat.name.split(' ')[0]}</div>
                <div style={s.summaryCount}>{count} req</div>
              </div>
              <div style={s.summaryBar2Track}>
                <div
                  style={{
                    ...s.summaryBar2Fill,
                    width: `${pct}%`,
                    background: cat.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Category Cards ── */}
      <div style={s.grid}>
        {shown.map((cat) => {
          const categoryReqCount = getCategoryRequestCount(cat);
          const isHov = hoveredCard === cat.id;
          return (
            <div
              key={cat.id}
              style={{
                ...s.card,
                borderTop: `3px solid ${cat.color}`,
                boxShadow: isHov
                  ? `0 12px 32px ${cat.color}22, var(--sc-shadow-lg)`
                  : 'var(--sc-shadow-md)',
                transform: isHov ? 'translateY(-2px)' : 'none',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={() => setHoveredCard(cat.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Header */}
              <div style={s.cardHeader}>
                <div style={s.catIcon}>
                  <span style={{ fontSize: 26 }}>{cat.icon}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2 style={s.catName}>{cat.name}</h2>
                  <div style={s.catBadges}>
                    <span style={{ ...s.badge, background: cat.color + '18', color: cat.color }}>
                      {cat.services.length} services
                    </span>
                    <span style={{ ...s.badge, background: 'var(--sc-warning-bg)', color: 'var(--sc-warning-text)' }}>
                      {categoryReqCount} requests
                    </span>
                  </div>
                </div>
              </div>

              <p style={s.catDesc}>{cat.description}</p>

              {/* Services with per-service request counts */}
              <div style={s.servicesLabel}>Service Types & Demand</div>
              <div style={s.servicesList}>
                {cat.services.map((service) => {
                  const count = reqByType[service] || 0;
                  const maxInCat = Math.max(...cat.services.map((sv) => reqByType[sv] || 0), 1);
                  const pct = (count / maxInCat) * 100;
                  return (
                    <div key={service} style={s.serviceRow}>
                      <div style={s.serviceNameCell}>{service}</div>
                      <div style={s.serviceBarTrack}>
                        <div
                          style={{
                            ...s.serviceBarFill,
                            width: `${pct}%`,
                            background: `linear-gradient(90deg, ${cat.color}88, ${cat.color})`,
                          }}
                        />
                      </div>
                      <div style={{ ...s.serviceCount, color: count > 0 ? cat.color : 'var(--sc-text-tertiary)' }}>
                        {count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    flexWrap: 'wrap' as const,
    gap: 16,
  },
  pageTitle: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  pageSubtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0, maxWidth: 560 },

  statsRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  statPill: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    background: 'var(--sc-bg-secondary)',
    borderRadius: 12,
    padding: '10px 20px',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-sm)',
    minWidth: 80,
  },
  statNum: { fontSize: 22, fontWeight: 800 },
  statLabel: { fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 500 },

  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    overflowX: 'auto',
    paddingBottom: 6,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  filterTab: {
    padding: '8px 14px',
    borderRadius: 20,
    border: '1px solid var(--sc-border)',
    background: 'var(--sc-bg-secondary)',
    color: 'var(--sc-text-secondary)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  filterTabActive: {
    background: 'var(--sc-brand-primary-bg)',
    borderColor: 'var(--sc-brand-primary)',
    color: 'var(--sc-brand-primary)',
  },

  summaryBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    background: 'var(--sc-bg-secondary)',
    borderRadius: 14,
    padding: '18px 20px',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-md)',
    marginBottom: 24,
  },
  summarySegment: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  summaryIcon: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: 'var(--sc-bg-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    flexShrink: 0,
  },
  summaryMeta: { display: 'flex', flexDirection: 'column' as const, minWidth: 0, flex: 1 },
  summaryName: { fontSize: 12, fontWeight: 600, color: 'var(--sc-text-primary)', whiteSpace: 'nowrap' as const },
  summaryCount: { fontSize: 11, color: 'var(--sc-text-tertiary)' },
  summaryBar2Track: { width: 48, height: 5, background: 'var(--sc-bg-primary)', borderRadius: 3, overflow: 'hidden' },
  summaryBar2Fill: { height: '100%', borderRadius: 3, transition: 'width 0.5s ease', minWidth: 2 },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 24,
  },

  card: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 14,
    padding: 26,
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-md)',
  },

  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 },
  catIcon: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: 'var(--sc-bg-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    flexShrink: 0,
  },
  catName: { fontSize: 16, fontWeight: 750, color: 'var(--sc-text-primary)', margin: '0 0 6px', fontFamily: "'Outfit', sans-serif" },
  catBadges: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  badge: {
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
  },

  catDesc: { fontSize: 13, color: 'var(--sc-text-secondary)', lineHeight: 1.6, margin: '0 0 16px' },

  servicesLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--sc-text-tertiary)',
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
    marginBottom: 10,
  },
  servicesList: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  serviceRow: { display: 'flex', alignItems: 'center', gap: 10 },
  serviceNameCell: { flex: 2, fontSize: 12, fontWeight: 500, color: 'var(--sc-text-primary)' },
  serviceBarTrack: {
    flex: 1,
    height: 6,
    background: 'var(--sc-bg-primary)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  serviceBarFill: { height: '100%', borderRadius: 3, minWidth: 3, transition: 'width 0.5s ease' },
  serviceCount: { width: 28, textAlign: 'right' as const, fontSize: 12, fontWeight: 700 },
};

